import React from 'react';
import { Alert, Box, Button, capitalize, Typography } from '@mui/material';
import { formatDistanceToNow, parseJSON } from 'date-fns';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth, useAccount } from '../../auth';
import { useEndAssumedRole } from '../../data/useInternalAdmin';
import type { ActiveAssumedRole } from '../../types/global.types';

const ROLE_LABELS: Record<string, string> = {
  viewer: 'Viewer',
  creator: 'Creator',
  'account-admin': 'Admin',
};

/**
 * Shows a warning banner when the current user is viewing an account
 * via an assumed role (not a real AccountRole).
 * Rendered inside MainLayout, above the Outlet.
 */
const AssumedRoleBanner: React.FC = () => {
  const auth = useAuth();
  const { currentAccount } = useAccount();
  const navigate = useNavigate();
  const endRole = useEndAssumedRole();
  const [, setTick] = React.useState(0);

  const assumedRoles: ActiveAssumedRole[] =
    auth.user?.active_assumed_roles || [];

  // Find an active assumed role matching the current account
  const match = currentAccount
    ? assumedRoles.find(
        (ar: ActiveAssumedRole) =>
          ar.account_id === currentAccount.id ||
          ar.account_name === currentAccount.name,
      )
    : null;

  // Check whether the user has a *real* AccountRole on this account.
  // `/users/me` now surfaces active assumed-role grants as `roles`
  // entries too (flagged `via_assumed_role`) so app account-pickers
  // list them — but those don't count as a real role here, otherwise
  // the banner would suppress itself on the very account it's meant to
  // warn about.
  const hasRealRole = currentAccount
    ? auth.user?.roles?.some(
        (r: any) =>
          (r.account.id === currentAccount.id ||
            r.account.name === currentAccount.name) &&
          !r.via_assumed_role,
      )
    : false;

  // Update the countdown every 60 seconds
  React.useEffect(() => {
    if (!match) return;
    const interval = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(interval);
  }, [match?.id]);

  // Only show when user is on an assumed role (no real role)
  if (!match || hasRealRole) return null;

  const roleLabel = ROLE_LABELS[match.assumed_role] || match.assumed_role;
  const accountLabel = match.account_title || match.account_name;
  const expiresIn = formatDistanceToNow(parseJSON(match.expires_at), {
    addSuffix: true,
  });

  const handleRelease = () => {
    endRole.mutate(match.id, {
      onSuccess: () => {
        navigate('/internal');
      },
    });
  };

  return (
    <Alert
      severity="warning"
      variant="filled"
      sx={{
        mb: 2,
        borderRadius: 1,
        alignItems: 'center',
        '& .MuiAlert-message': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          Assuming {roleLabel} role on {accountLabel}
          {match.reason ? ` \u2014 ${match.reason}` : ''}
          {' \u2014 expires '}
          {expiresIn}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          onClick={handleRelease}
          disabled={endRole.isPending}
          sx={{ ml: 2, whiteSpace: 'nowrap' }}
        >
          {endRole.isPending ? 'Deactivating...' : 'Deactivate Role'}
        </Button>
      </Box>
    </Alert>
  );
};

export default AssumedRoleBanner;
